import React from "react";

import { IconActivity, IconChecklist, IconMessage, IconTarget } from "turboui";

//
// Notification categories for the "Category lanes / digest" layout.
//
// Every notification carries `activity.action` (one of ~66 action strings).
// We classify each into exactly one lane via `categoryOf` (first match wins),
// so the rail can filter and the "All" view can render a grouped digest.
//
// Note: there is intentionally no "Reactions" lane — the backend does not emit
// a reaction notification action today, so such a lane would always be empty.
// True @mentions are not a separate action either; they live inside comments,
// so the "Mentions & replies" lane is backed by comment/reply actions.
//

export type CategoryId = "all" | "mentions" | "assigned" | "checkins" | "discussions" | "updates";

export interface CategoryMeta {
  id: CategoryId;
  label: string;
  icon: React.ReactNode;
  /** classes for the small square type tile (bg + text) */
  tile: string;
}

export const CATEGORY_META: Record<CategoryId, CategoryMeta> = {
  all: { id: "all", label: "All", icon: null, tile: "" },
  mentions: {
    id: "mentions",
    label: "Mentions & replies",
    icon: <span className="text-[12px] font-bold leading-none">@</span>,
    tile: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  },
  assigned: {
    id: "assigned",
    label: "Assigned",
    icon: <IconChecklist size={13} />,
    tile: "bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300",
  },
  checkins: {
    id: "checkins",
    label: "Check-ins & reviews",
    icon: <IconTarget size={13} />,
    tile: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  },
  discussions: {
    id: "discussions",
    label: "Discussions",
    icon: <IconMessage size={13} />,
    tile: "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  },
  updates: {
    id: "updates",
    label: "Updates",
    icon: <IconActivity size={13} />,
    tile: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  },
};

// Ordered, non-"all" lanes — drives both the digest order and first-match classification.
export const CATEGORY_ORDER: Exclude<CategoryId, "all">[] = [
  "mentions",
  "assigned",
  "checkins",
  "discussions",
  "updates",
];

export function isCategoryId(value: string | null): value is CategoryId {
  return value === "all" || (CATEGORY_ORDER as string[]).includes(value ?? "");
}

export function categoryOf(action: string | null | undefined): Exclude<CategoryId, "all"> {
  const a = action ?? "";

  // Comments / replies (where @mentions surface)
  if (a === "comment_added" || a.endsWith("_commented") || a.endsWith("_comment_submitted")) {
    return "mentions";
  }

  // Things assigned to you: tasks, reviewers, champions, contributors
  if (
    a === "task_adding" ||
    a === "task_assignee_updating" ||
    a.includes("champion_updating") ||
    a.includes("reviewer_updating") ||
    a.includes("contributor_addition") ||
    a === "project_contributor_edited"
  ) {
    return "assigned";
  }

  // Check-ins & review requests
  if (a.includes("check_in")) {
    return "checkins";
  }

  // Discussions / messages
  if (a.includes("discussion")) {
    return "discussions";
  }

  // Everything else (status changes, team/space, documents, milestones, …)
  return "updates";
}
